'use client';

import CommonLoading from '@/components/layouts/CommonLoading/CommonLoading';
import BreadCrumb from '@/components/ui/BreadCrumb/BreadCrumb';
import { InfiniteScroll } from '@/components/ui/InfiniteScroll/InfiniteScroll';
import ItemFilterTrigger from '@/components/ui/ItemFilterTrigger/ItemFilterTrigger';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import ScrollToTopButton from '@/components/ui/ScrollToTopButton/ScrollToTopButton';
import UserDesignDetailModal from '@/features/gfUserDesign/components/UserDesignDetailModal/UserDesignDetailModal';
import { SIZE_SELECT_FROM } from '@/features/gfUserDesign/constants';
import type { GfMaterialCountsResult } from '@/features/gfWorks/api/getMaterialCounts';
import { useMaterialLoader } from '@/features/gfWorks/hooks/useMaterialLoader';
import type {
  MaterialSequenceApiParams,
  MaterialSequenceInitialState,
} from '@/features/gfWorks/hooks/useMaterialSequence';
import type {
  GfMaterialCategoryItem,
  GfMaterialDetail,
  GfMaterialPopularList,
  ItemFilterProduct,
} from '@/features/gfWorks/types';
import { materialDetailToItem } from '@/features/gfWorks/utils/materialDetailToItem';
import { useIsSp } from '@/hooks/useMediaQuery';
import { swal } from '@/lib/sweetalert';
import { useGlobalLoadingAtom } from '@/state';
import layoutStyles from '@/styles/modules/layout.module.scss';
import utilsStyles from '@/styles/modules/utils.module.scss';
import type { UserDesignItem } from '@/types';
import { sendMaterialClickEvent } from '@/utils/dualTrackEvents';
import { formatCreatorNames } from '@/utils/formatters';
import { sendMaterialFavoriteClickEvent } from '@/utils/ga';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { buildProductRoute } from '@/utils/worksRoute';
import type { Route } from 'next';
import { useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { getMaterialDetail } from '../../api/getMaterialDetail';
import { MATERIAL_CATEGORY_ALL } from '../../api/getMaterialListByTypeInfinite';
import type { GfWorkDetail } from '../../types';
import ItemFilterModal from '../ItemFilterModal/ItemFilterModal';
import MaterialCardList from '../MaterialCardList/MaterialCardList';
import MaterialDetailModal from '../MaterialDetailModal/MaterialDetailModal';
import WorkDetailSidebar from '../WorkDetailSidebar/WorkDetailSidebar';
import styles from './MaterialContainer.module.scss';

function MaterialContainer({
  materialCounts,
  initialMaterialsForAll,
  popularMaterials,
  products,
  work,
  workId,
  workName,
  workImagePath,
  userDesigns,
  pageLimit,
  selectedMaterialId,
  selectedTagId,
  selectedProductId,
  initialSelectedMaterial,
  initialOpenUserPrintId,
}: {
  materialCounts: GfMaterialCountsResult;
  initialMaterialsForAll: GfMaterialCategoryItem | null;
  popularMaterials: GfMaterialPopularList;
  products: ItemFilterProduct[];
  work: GfWorkDetail;
  workId: number;
  workName: string;
  workImagePath?: string;
  userDesigns: UserDesignItem[];
  pageLimit: number;
  selectedMaterialId?: string;
  selectedTagId: number | null;
  selectedProductId: number | null;
  initialSelectedMaterial?: GfMaterialDetail | null;
  initialOpenUserPrintId?: string;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const session = useSession();
  const [, startGlobalLoading] = useGlobalLoadingAtom();
  // URLパラメータの更新処理中かどうか（フィルター切り替え時）
  const [isPending, startTransition] = useTransition();

  const {
    currentMaterials,
    hasMore,
    totalCount,
    isLoading,
    isLoadingMore,
    loadMore,
    updateFavoriteInCache,
    allMaterialsFlat,
    setLoadingForProductId,
  } = useMaterialLoader({
    workId,
    initialAll: initialMaterialsForAll,
    pageLimit,
    selectedTagId,
    selectedProductId,
    materialCounts,
  });

  // みんなのデザイン: PC/SPで表示件数を切り替え
  const isSp = useIsSp();
  const displayDesigns = useMemo(() => {
    const limit = isSp ? 10 : 5;
    return userDesigns.slice(0, limit);
  }, [userDesigns, isSp]);

  // みんなのデザイン詳細モーダル（表示件数分のみスライド可能）
  const [isUserDesignModalOpen, setIsUserDesignModalOpen] = useState(false);
  const [selectedDesignIndex, setSelectedDesignIndex] = useState(0);
  const handleDesignClick = useCallback(
    (design: UserDesignItem) => {
      const index = displayDesigns.findIndex((d) => d.id === design.id);
      if (index >= 0) {
        setSelectedDesignIndex(index);
        setIsUserDesignModalOpen(true);
      }
    },
    [displayDesigns],
  );
  const getDesignCallbackUrl = useCallback(
    (item: UserDesignItem) => `/works/${workId}?openModal=${item.userPrintId}` as Route,
    [workId],
  );

  // openModal パラメータがある場合、デザイン詳細モーダルを自動起動
  useEffect(() => {
    if (!initialOpenUserPrintId || displayDesigns.length === 0) return;
    const index = displayDesigns.findIndex((d) => d.userPrintId === initialOpenUserPrintId);
    if (index >= 0) {
      setSelectedDesignIndex(index);
      setIsUserDesignModalOpen(true);
    }
  }, [initialOpenUserPrintId, displayDesigns]);

  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [isItemFilterOpen, setIsItemFilterOpen] = useState(false);
  // モーダルを開いた時点の初期状態。key は開くたびに新しい値にして、同じコマを開き直しても必ず作り直す。
  const [materialModalState, setMaterialModalState] = useState<{
    key: number;
    initialState: MaterialSequenceInitialState;
    apiParams: MaterialSequenceApiParams;
    // 商品フィルター無しの一覧クリックで即開くときは未指定（モーダルが loadDetail で遅延取得）。
    // フィルター適用中・URL 直開き / SNS 共有では取得済みの詳細を渡す
    initialDetail?: GfMaterialDetail;
  } | null>(null);
  // モーダルを開いた回数。materialModalState.key に使い、同じコマの再オープンでも再マウントさせる。
  const modalOpenSeqRef = useRef(0);
  // handleOpen を安定させたまま最新の一覧/hasMore を参照する ref（依存に入れると URL 起動 effect が再発火する）
  const currentMaterialsRef = useRef(currentMaterials);
  currentMaterialsRef.current = currentMaterials;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const [popularMaterialList, setPopularMaterialList] = useState<GfMaterialPopularList>(popularMaterials);

  const selectedProduct = useMemo(
    () => (selectedProductId !== null ? (products.find((p) => p.id === selectedProductId) ?? null) : null),
    [selectedProductId, products],
  );

  const selectedProductIds = useMemo(
    () => (selectedProductId !== null ? [selectedProductId] : []),
    [selectedProductId],
  );

  const navigateToProduct = useCallback(
    (productId: number, materialId: number, workIdForUrl: number, fromFilter: boolean) => {
      startGlobalLoading();
      const isFromFilter = fromFilter && selectedProductId !== null;
      router.push(
        buildProductRoute({
          productId,
          materialId,
          workId: workIdForUrl,
          filteredProductId: isFromFilter ? selectedProductId : undefined,
          isFromFilter,
        }),
      );
    },
    [startGlobalLoading, router, selectedProductId],
  );

  const handleOpenMaterialDetailModal = useCallback(
    async (materialId: number, preloaded?: GfMaterialDetail) => {
      const userId = session?.data?.user.id ?? 0;
      const apiParams: MaterialSequenceApiParams = {
        workId,
        categoryName: MATERIAL_CATEGORY_ALL,
        limit: pageLimit,
        tagIds: selectedTagId !== null ? [selectedTagId] : undefined,
        gfProductId: selectedProductId ?? undefined,
      };
      const loadedMaterials = currentMaterialsRef.current;
      const openedIndex = loadedMaterials.findIndex((m) => m.id === materialId);
      // 一覧から開くときの初期並び（openedIndex >= 0 のときだけ参照する）。前後は API で読み足す
      const gridInitialState: MaterialSequenceInitialState = {
        items: loadedMaterials,
        index: openedIndex,
        hasPrev: false,
        hasNext: hasMoreRef.current,
      };

      // 商品フィルター無し & 一覧内のコマ → 詳細取得（利用可能アイテム）を待たずに即開く。
      // 画像・話数は一覧データで出せるため、クリックからモーダル表示までのラグを無くせる。
      // 利用可能アイテムはモーダルが loadDetail で遅延取得する（PC/SP 共通。回線が細い SP ほど効く）。
      // selectedProductId のみで判定するのは、下のモーダルスキップ（availableProducts 照合）が
      // 商品フィルターにのみ依存するため。タグ絞り込み(selectedTagId)中も一覧内コマなら即オープンでよい。
      const canOpenWithoutDetail = selectedProductId === null && openedIndex >= 0 && !preloaded;
      if (canOpenWithoutDetail) {
        const openedItem = loadedMaterials[openedIndex];
        sendMaterialClickEvent({ materialId, materialName: openedItem?.name ?? '', workId, workName, userId });
        modalOpenSeqRef.current += 1;
        setMaterialModalState({
          key: modalOpenSeqRef.current,
          initialState: gridInitialState,
          apiParams,
        });
        setIsMaterialModalOpen(true);
        return;
      }

      // フィルター適用中（スキップ判定に availableProducts が要る）／一覧に居ない（SNS 共有・URL 直開き）
      // → 詳細を取ってから判断・表示する
      try {
        const material = preloaded ?? (await getMaterialDetail(materialId)).result;
        sendMaterialClickEvent({ materialId, materialName: material.name ?? '', workId, workName, userId });

        // アイテムフィルター済みかつ該当商品が利用可能 → モーダルスキップで直接遷移
        if (selectedProductId !== null) {
          const matched = material.availableProducts.find((p) => p.id === selectedProductId);
          if (matched) {
            navigateToProduct(matched.id, material.id, workId, true);
            return;
          }
        }

        // 前後送りの初期の並び。開いたコマが読み込み済みの一覧にあればその一覧を、
        // 無ければ（SNS 共有・URL 直開き）そのコマ 1 件だけを初期の並びにして、前後は API で読み足す。
        const initialState: MaterialSequenceInitialState =
          openedIndex >= 0
            ? gridInitialState
            : { items: [materialDetailToItem(material)], index: 0, hasPrev: true, hasNext: true };
        modalOpenSeqRef.current += 1;
        setMaterialModalState({
          key: modalOpenSeqRef.current,
          initialState,
          apiParams,
          initialDetail: material,
        });
        setIsMaterialModalOpen(true);
      } catch {
        setIsMaterialModalOpen(false);
        await swal.alert({
          text: 'コマの詳細情報を取得できませんでした。',
        });
      }
    },
    [session?.data?.user.id, selectedProductId, selectedTagId, workId, workName, pageLimit, navigateToProduct],
  );

  // 同じ selectedMaterialId で二重に自動オープンしないための番人。
  // お気に入り操作の router.refresh() で initialSelectedMaterial の参照が変わっても再オープン・再計測させない。
  const autoOpenedMaterialIdRef = useRef<string | null>(null);

  // selectedMaterialId がURLにある場合、デフォルトで素材モーダルを開く
  useEffect(() => {
    if (!selectedMaterialId) {
      autoOpenedMaterialIdRef.current = null;
      return;
    }
    if (autoOpenedMaterialIdRef.current === selectedMaterialId) return;
    const materialId = Number(selectedMaterialId);
    if (Number.isNaN(materialId)) return;
    autoOpenedMaterialIdRef.current = selectedMaterialId;
    // サーバーで事前取得済みならAPIを叩かず即表示
    const preloaded = initialSelectedMaterial?.id === materialId ? initialSelectedMaterial : undefined;
    handleOpenMaterialDetailModal(materialId, preloaded);
  }, [selectedMaterialId, initialSelectedMaterial, handleOpenMaterialDetailModal]);

  // モーダルを閉じる際に selectedMaterialId をURLから除去し、ブラウザバック時の再表示を防ぐ
  const handleSetMaterialModalOpen = useCallback(
    (isOpen: boolean) => {
      setIsMaterialModalOpen(isOpen);
      if (!isOpen && searchParams.has('selectedMaterialId')) {
        const params = new URLSearchParams(searchParams);
        params.delete('selectedMaterialId');
        router.replace(`?${params.toString()}`, { scroll: false });
      }
    },
    [searchParams, router],
  );

  const handleItemFilterToggle = useCallback(
    (productId: number | null) => {
      const isDeselect = productId === null || selectedProductId === productId;
      const newProductId = isDeselect ? null : productId;
      const params = new URLSearchParams(searchParams);

      setLoadingForProductId(newProductId);

      startTransition(() => {
        if (newProductId === null) params.delete('productId');
        else params.set('productId', newProductId.toString());
        router.replace(`?${params.toString()}`, { scroll: false });
      });

      setIsItemFilterOpen(false);
    },
    [searchParams, selectedProductId, setLoadingForProductId, router],
  );

  const handleRemoveItemFilter = useCallback(() => {
    if (selectedProductId === null) return;
    const params = new URLSearchParams(searchParams);

    setLoadingForProductId(null);

    startTransition(() => {
      params.delete('productId');
      router.replace(`?${params.toString()}`, { scroll: false });
    });
  }, [selectedProductId, searchParams, setLoadingForProductId, router]);

  const closeItemFilter = useCallback(() => setIsItemFilterOpen(false), []);

  // お気に入り変更時に、一覧キャッシュ・人気一覧・計測へ反映する（モーダル内の表示はモーダルが更新）
  const handleFavoriteChanged = (materialId: number, isFavorite: boolean) => {
    updateFavoriteInCache(materialId, isFavorite);
    setPopularMaterialList((prev) => prev.map((m) => (m.id === materialId ? { ...m, isFavorite } : m)));
    const found =
      allMaterialsFlat.find((m) => m.id === materialId) ?? popularMaterialList.find((m) => m.id === materialId);
    const userId = session?.data?.user.id ?? 0;
    sendMaterialFavoriteClickEvent(materialId, found?.name ?? '', isFavorite, userId);
  };

  // グリッドのカードでお気に入りを押したとき（API はカード側で実行済み）。現在値を反転して反映に委譲する。
  const toggleGridFavorite = (materialId: number) => {
    const current =
      (allMaterialsFlat.find((m) => m.id === materialId) ?? popularMaterialList.find((m) => m.id === materialId))
        ?.isFavorite ?? false;
    handleFavoriteChanged(materialId, !current);
  };

  // コマ表示フラグ（素材がある場合は表示し続ける。isPending中もDOMを残してスクロール位置を維持する）
  const isShowMaterials = currentMaterials.length > 0;
  // フィルター適用後などで該当コマが0件のとき（ローディング中を除く）は空状態を表示する
  const isShowEmpty = !isLoading && !isPending && !isShowMaterials;

  return (
    <>
      <div className={layoutStyles.breadCrumbContainer}>
        <BreadCrumb items={[{ text: '作品一覧', href: '/works?sort=created_at_desc' }, { text: workName }]} />
      </div>

      {/* SP: 作品概要（PCはサイドバーに移動） */}
      <div className={utilsStyles.onlySp}>
        <div className={`${layoutStyles.gfPageContainerLarge} ${styles.workOverviewSection}`}>
          <div className={styles.workOverviewContent}>
            {workImagePath && (
              <div className={styles.workImageContainer}>
                <NextImageView
                  src={workImagePath}
                  alt={workName}
                  fill
                  sizes={getImageLayoutSizes({ sp: '33vw', pc: '360px' })}
                  objectFit='contain'
                />
              </div>
            )}
            <div className={styles.workOverviewRightColumn}>
              <div className={styles.workOverviewTitleContainer}>
                <h2 className={styles.workOverviewTitle}>{workName}</h2>
                <p className={styles.workOverviewAuthor}>{formatCreatorNames(work.creators)}</p>
                <p className={styles.workOverviewSource}>
                  <span className={styles.workOverviewSourceLabel}>コマデータ出典元：</span>
                  {work.source}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PC: 2カラムレイアウト / SP: 1カラム */}
      <div className={`${layoutStyles.gfPageContainerLargeNoPaddingSp} ${utilsStyles.gfMtMedium} ${styles.pageLayout}`}>
        {/* PC: 左サイドバー */}
        <WorkDetailSidebar work={work} />

        {/* メインコンテンツ */}
        <div className={styles.mainContent}>
          {/* みんなのデザインセクション */}
          {userDesigns.length > 0 && (
            <div className={styles.userDesignSection}>
              <h3 className={styles.sectionTitle}>
                みんなの<span className={styles.accentText}>デザイン</span>から探す
              </h3>
              <div className={styles.userDesignList}>
                {displayDesigns.map((design) => (
                  <button
                    type='button'
                    key={design.id}
                    className={styles.userDesignItem}
                    onClick={() => handleDesignClick(design)}
                  >
                    <div className={styles.userDesignImageContainer}>
                      <NextImageView
                        src={design.previewImageUrl}
                        alt={`${design.gfWorkName} - ${design.productName}`}
                        fill
                        sizes={getImageLayoutSizes({ sp: '28vw', pc: '180px' })}
                        objectFit='contain'
                      />
                    </div>
                  </button>
                ))}
                {/* SP: スライダー末尾「もっとみる」カード */}
                <LoadingLink href={`/works/${workId}/user-designs`} className={styles.userDesignMoreCard}>
                  <RoundArrowIcon color='black' direction='right' size='xLarge' borderColor='black' />
                  もっとみる
                </LoadingLink>
              </div>
              {/* PC: ボタン型「もっとみる」 */}
              <div className={styles.userDesignMoreButtonContainer}>
                <LoadingLink href={`/works/${workId}/user-designs`} className={styles.userDesignMoreButton}>
                  もっとみる
                  <RoundArrowIcon color='red' direction='right' />
                </LoadingLink>
              </div>
              <UserDesignDetailModal
                isOpen={isUserDesignModalOpen}
                onClose={() => setIsUserDesignModalOpen(false)}
                items={displayDesigns}
                initialIndex={selectedDesignIndex}
                getCallbackUrl={getDesignCallbackUrl}
                from={SIZE_SELECT_FROM.userDesigns}
              />
            </div>
          )}

          <div className={styles.materialListContainer}>
            <h3 className={styles.sectionTitle}>
              好きな<span className={styles.accentText}>コマ</span>や
              <span className={styles.accentText}>キャラクター</span>を探す
            </h3>

            {/* アイテムフィルター（PC/SP 共通: ボタン→ドロワー） */}
            <div className={styles.filterSection}>
              <ItemFilterTrigger
                label='つくりたいアイテムで絞り込む'
                selectedProductName={selectedProduct?.name}
                onOpen={() => setIsItemFilterOpen(true)}
                onClear={handleRemoveItemFilter}
                disabled={isLoading || isPending}
              />
            </div>

            <div className={styles.materialContainer}>
              {(isLoading || isPending) && (
                <div className={styles.loadingContainer}>
                  <CommonLoading />
                </div>
              )}
              {isShowMaterials && (
                <>
                  <p className={styles.materialTotalCount}>{totalCount}件</p>
                  <div className={`${styles.materialGridSection} ${isPending ? styles.materialGridPending : ''}`}>
                    {isPending && <div className={styles.materialGridOverlay} />}
                    <MaterialCardList
                      materials={currentMaterials}
                      onMaterialClick={handleOpenMaterialDetailModal}
                      toggleFavoriteState={toggleGridFavorite}
                      bgColor='gray200'
                      materialNameDisplay='overlay'
                    />
                    <InfiniteScroll
                      hasMore={hasMore}
                      isLoading={isLoadingMore}
                      onLoadMore={loadMore}
                      loadingComponent={
                        <div className={styles.loadingContainer}>
                          <CommonLoading />
                        </div>
                      }
                      sentinelClassName={styles.loadMoreSentinel}
                    />
                  </div>
                </>
              )}
              {isShowEmpty && (
                <>
                  <p className={styles.materialTotalCount}>{totalCount}件</p>
                  <p className={styles.materialEmptyMessage}>該当するコマがありませんでした</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={utilsStyles.onlySp}>
        <ScrollToTopButton />
      </div>
      {materialModalState && (
        <MaterialDetailModal
          key={materialModalState.key}
          isOpen={isMaterialModalOpen}
          onClose={() => handleSetMaterialModalOpen(false)}
          initialState={materialModalState.initialState}
          apiParams={materialModalState.apiParams}
          initialDetail={materialModalState.initialDetail}
          filteredProductId={selectedProductId ?? undefined}
          onFavoriteChanged={handleFavoriteChanged}
        />
      )}
      <ItemFilterModal
        isOpen={isItemFilterOpen}
        onClose={closeItemFilter}
        products={products}
        selectedProductIds={selectedProductIds}
        onToggleProduct={handleItemFilterToggle}
        onClear={handleRemoveItemFilter}
      />
    </>
  );
}

export default MaterialContainer;
